import { useState, useEffect, useCallback } from "react";
import { Loader2, Phone, RefreshCw, CheckCircle2, UserPlus, PackageCheck, MessageSquare, X, AlertTriangle, Clock, Download } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "needs_attention", label: "Needs Attention" },
  { id: "in_progress", label: "In Progress" },
  { id: "placed", label: "Placed" },
  { id: "completed", label: "Completed" },
  { id: "cancelled", label: "Cancelled" },
];
const FAILURES = ["search_empty", "provider_error", "auth_failed", "address_unserviceable", "payment_failed", "timeout", "user_frustrated"];
const JOURNEYS = ["food_order", "grocery_order", "medicine_order", "home_visit", "cab"];

const timeSince = (iso) => {
  if (!iso) return "—";
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.floor(m / 60)}h ago`;
  return `${Math.floor(m / 1440)}d ago`;
};

function MetricsStrip({ m }) {
  if (!m) return null;
  const chips = [
    { label: "Drafts today", value: m.drafts, cls: "bg-gray-100 text-gray-700" },
    { label: "Placed", value: m.placed, cls: "bg-emerald-100 text-emerald-700" },
    { label: "Failed", value: m.failed, cls: "bg-red-100 text-red-700" },
    { label: "Abandoned", value: m.abandoned, cls: "bg-amber-100 text-amber-700" },
  ];
  return (
    <div className="bg-white rounded-xl border shadow-sm p-4 mb-4" data-testid="orders-metrics">
      <div className="flex flex-wrap gap-2 mb-3">
        {chips.map((c) => (
          <div key={c.label} className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${c.cls}`}>
            {c.value} <span className="font-normal opacity-80">{c.label}</span>
          </div>
        ))}
      </div>
      {m.per_journey?.length > 0 && (
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-600">
          {m.per_journey.map((j) => (
            <span key={j.journey}>{j.label}: <b className="text-gray-900">{j.conversion}%</b> ({j.placed}/{j.total})</span>
          ))}
        </div>
      )}
      {m.top_failures?.length > 0 && (
        <div className="mt-2 text-xs text-gray-500">Top issues: {m.top_failures.map((f) => `${f.label} (${f.count})`).join(" · ")}</div>
      )}
    </div>
  );
}

function OrderCard({ o, onOpen }) {
  const attn = o.needs_attention;
  return (
    <button
      type="button"
      onClick={() => onOpen(o)}
      className={`w-full text-left bg-white rounded-xl p-4 border shadow-sm transition-colors hover:border-[#2D2766] ${attn ? "border-l-4 border-l-red-500" : ""}`}
      data-testid={`order-card-${o.order_id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-lg shrink-0">{o.journey_icon}</span>
          <span className="font-semibold text-gray-900 truncate">{o.user_name || "Unknown"}</span>
          {o.phone && <span className="text-xs text-gray-400 font-mono">+{o.phone}</span>}
        </div>
        <span className="text-xs text-gray-400 whitespace-nowrap flex items-center gap-1"><Clock className="w-3 h-3" />{timeSince(o.updated_at)}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-1.5">
        <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">{o.journey_label}</span>
        <span className="text-xs px-2 py-0.5 rounded bg-indigo-50 text-indigo-700">{(o.status || "").replace(/_/g, " ")}</span>
        {o.failure_label && (
          <span className="text-xs px-2 py-0.5 rounded bg-red-50 text-red-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{o.failure_label}</span>
        )}
        {o.attempts > 0 && <span className="text-xs text-gray-400">{o.attempts} attempts</span>}
        {o.assigned_to && <span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700">@{o.assigned_to}</span>}
      </div>
      {o.slots_human?.length > 0 && (
        <p className="text-sm text-gray-700">{o.slots_human.join(" · ")}</p>
      )}
      {o.last_user_message && <p className="text-xs text-gray-400 mt-1 truncate">“{o.last_user_message}”</p>}
    </button>
  );
}

function Action({ icon: Icon, label, onClick, busy, cls = "" }) {
  return (
    <button type="button" onClick={onClick} disabled={busy}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50 ${cls}`}
      data-testid={`order-action-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />} {label}
    </button>
  );
}

function DispatchPanel({ orderId, order, onChanged }) {
  const isCab = order.journey === "cab";
  const [c, setC] = useState(order.contact || {});
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setC({ ...c, [k]: e.target.value });
  const post = async (path, body, okMsg) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/assistant-orders/${orderId}/${path}`, body || c);
      toast.success(r.data?.sent === false ? "No change — nothing re-sent" : okMsg);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const F = ({ k, ph }) => (
    <input value={c[k] || ""} onChange={set(k)} placeholder={ph}
      className="border rounded-lg px-2 py-1.5 text-sm w-full" data-testid={`dispatch-${k}`} />
  );
  return (
    <div className="border rounded-lg p-3" data-testid="dispatch-panel">
      <p className="text-xs font-semibold text-gray-700 mb-2">{isCab ? "🚕 Cab dispatch" : "🛵 Rider / contact details"}
        {order.contact?.source && <span className="ml-2 text-[10px] uppercase text-gray-400">source: {order.contact.source}</span>}</p>
      <div className="grid grid-cols-2 gap-2">
        <F k="name" ph="Name" /><F k="phone" ph="Phone" />
        <F k="vehicle" ph={isCab ? "Vehicle model" : "Vehicle"} />
        {isCab && <F k="colour" ph="Colour" />}
        {isCab && <F k="plate" ph="Number plate" />}
        {isCab && <F k="otp" ph="Pickup OTP" />}
        <F k="eta" ph="ETA (e.g. 1:20 pm)" />
        {isCab && <F k="fare" ph="Fare ₹" />}
        {!isCab && <F k="tracking_url" ph="Tracking link" />}
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        <button disabled={busy} onClick={() => post("dispatch", null, "Saved")} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-gray-800 disabled:opacity-50" data-testid="dispatch-save">Save</button>
        <button disabled={busy} onClick={() => post("dispatch/send", null, "Sent to customer")} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#2D2766] text-white disabled:opacity-50" data-testid="dispatch-send">Save & Send</button>
        {isCab && <>
          <button disabled={busy} onClick={() => post("dispatch/status", { action: "arrived" }, "Marked arrived")} className="px-3 py-1.5 rounded-lg text-xs bg-amber-100 text-amber-700" data-testid="dispatch-arrived">Arrived</button>
          <button disabled={busy} onClick={() => post("dispatch/status", { action: "completed" }, "Completed")} className="px-3 py-1.5 rounded-lg text-xs bg-emerald-100 text-emerald-700" data-testid="dispatch-completed">Completed</button>
          <button disabled={busy} onClick={() => { const r = window.prompt("Cancel reason:"); if (r) post("dispatch/status", { action: "cancelled", reason: r }, "Cancelled"); }} className="px-3 py-1.5 rounded-lg text-xs bg-red-100 text-red-700" data-testid="dispatch-cancelled">Cancel</button>
        </>}
      </div>
      {order.dispatch_log?.length > 0 && (
        <div className="mt-2 text-[11px] text-gray-400 space-y-0.5 max-h-24 overflow-y-auto" data-testid="dispatch-log">
          {order.dispatch_log.slice(-6).map((l, i) => <div key={i}>{timeSince(l.at)} · {l.by} · {l.action}{l.reason ? ` (${l.reason})` : ""}</div>)}
        </div>
      )}
    </div>
  );
}

function OrderDetail({ orderId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [showLogs, setShowLogs] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/assistant-orders/${orderId}`);
      setData(res.data);
    } catch { toast.error("Could not load order"); }
  }, [orderId]);
  useEffect(() => { load(); }, [load]);

  const act = async (path, body, okMsg) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/assistant-orders/${orderId}/${path}`, body || {});
      if (okMsg) toast.success(okMsg);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  const o = data?.order;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="order-detail-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-[#2D2766] text-white px-4 py-3 flex items-center justify-between">
          <span className="font-semibold">{o ? `${o.journey_icon} ${o.journey_label}` : "Order"}</span>
          <button onClick={onClose} data-testid="order-detail-close"><X className="w-5 h-5" /></button>
        </div>
        {!o ? <Loader2 className="w-6 h-6 animate-spin mx-auto mt-12 text-gray-400" /> : (
          <div className="p-4 space-y-4">
            <div>
              <p className="font-semibold text-gray-900">{o.user_name || "Unknown"} {o.phone && <span className="font-mono text-sm text-gray-500">+{o.phone}</span>}</p>
              <p className="text-sm text-gray-500 mt-0.5">{(o.status || "").replace(/_/g, " ")} · at step “{o.current_step}” · {o.attempts} attempts</p>
              {o.failure_label && <p className="text-sm text-red-600 mt-1">⚠ {o.failure_label}{o.failure_detail ? `: ${o.failure_detail}` : ""}</p>}
              {data.paused && <p className="text-xs text-amber-600 mt-1 font-medium">Bot paused — you are handling this chat</p>}
            </div>

            <div className="bg-gray-50 rounded-lg p-3">
              {(o.slots_human || []).map((l, i) => <p key={i} className="text-sm text-gray-800">{l}</p>)}
              {o.resolution_note && <p className="text-xs text-emerald-700 mt-2">Resolved: {o.resolution_note}</p>}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              {o.phone && <a href={`tel:+${o.phone}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-gray-800" data-testid="order-action-call"><Phone className="w-3.5 h-3.5" /> Call</a>}
              {["food_order", "grocery_order"].includes(o.journey) && <Action icon={RefreshCw} label="Retry step" busy={busy} onClick={() => act("retry", {}, "Retried")} cls="bg-sky-100 text-sky-700" />}
              <Action icon={PackageCheck} label="Place manually" busy={busy} onClick={() => {
                const pid = window.prompt("Provider order id:");
                if (!pid) return;
                act("place-manual", { provider_order_id: pid, note: window.prompt("Note (optional):") || "" }, "Marked placed");
              }} cls="bg-emerald-100 text-emerald-700" />
              <Action icon={UserPlus} label="Assign" busy={busy} onClick={() => {
                const who = window.prompt("Assign to:");
                if (who) act("assign", { assigned_to: who }, "Assigned");
              }} cls="bg-blue-100 text-blue-700" />
              {o.needs_attention && <Action icon={CheckCircle2} label="Resolve" busy={busy} onClick={() => {
                const note = window.prompt("Resolution note (required):");
                if (note?.trim()) act("resolve", { resolution_note: note }, "Resolved");
              }} cls="bg-green-600 text-white" />}
            </div>

            {/* Agent takeover */}
            <div className="border rounded-lg p-3">
              <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> Message on WhatsApp (pauses the bot)</p>
              <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={2}
                className="w-full border rounded-lg px-2 py-1.5 text-sm" placeholder="Type a reply to the senior…" data-testid="order-agent-message-input" />
              <div className="flex gap-2 mt-2">
                <button disabled={busy || !msg.trim()} onClick={() => act("message", { text: msg }, "Sent").then(() => setMsg(""))}
                  className="flex-1 bg-[#2D2766] text-white text-sm py-1.5 rounded-lg disabled:opacity-50" data-testid="order-agent-send-btn">Send & pause bot</button>
                {data.paused && <button disabled={busy} onClick={() => act("resume", { note: window.prompt("Closing note to the senior (optional):") || "" }, "Bot resumed")}
                  className="px-3 bg-amber-500 text-white text-sm py-1.5 rounded-lg disabled:opacity-50" data-testid="order-agent-resume-btn">Close</button>}
              </div>
            </div>

            {/* Dispatch / contact */}
            <DispatchPanel orderId={orderId} order={o} onChanged={() => { load(); onChanged?.(); }} />

            {/* Timeline */}
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-2">Conversation</p>
              <div className="space-y-1.5 max-h-56 overflow-y-auto" data-testid="order-timeline">
                {(data.timeline || []).map((t) => (
                  <div key={t.id} className={`text-sm flex ${t.direction === "in" ? "justify-end" : "justify-start"}`}>
                    <span className={`px-2.5 py-1.5 rounded-lg max-w-[85%] ${t.direction === "in" ? "bg-[#EDE8F8] text-gray-900" : "bg-gray-100 text-gray-700"}`}>{t.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Tool logs */}
            <div>
              <button onClick={() => setShowLogs((s) => !s)} className="text-xs font-semibold text-[#2D2766]" data-testid="order-toollogs-toggle">
                {showLogs ? "Hide" : "Show"} tool logs ({data.tool_logs?.length || 0})
              </button>
              {showLogs && (
                <div className="mt-2 space-y-1.5 max-h-56 overflow-y-auto" data-testid="order-tool-logs">
                  {(data.tool_logs || []).map((l, i) => (
                    <div key={i} className={`text-xs border rounded p-2 ${l.error ? "border-red-200 bg-red-50" : "border-gray-100"}`}>
                      <div className="flex justify-between"><span className="font-mono font-semibold">{l.tool}</span><span className={l.error ? "text-red-600" : "text-gray-400"}>{l.status ?? "—"} · {l.latency_ms}ms</span></div>
                      <div className="text-gray-500 truncate">q: {l.inputs?.query || l.inputs?.addressId || "—"}</div>
                      {l.error && <div className="text-red-600 truncate">{l.error}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminOrdersBoard() {
  const [tab, setTab] = useState("needs_attention");
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({});
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [journey, setJourney] = useState("");
  const [failure, setFailure] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ tab });
      if (journey) params.set("journey", journey);
      if (failure) params.set("failure_reason", failure);
      if (q) params.set("q", q);
      const [list, m] = await Promise.all([
        axios.get(`${API}/admin/assistant-orders?${params}`),
        axios.get(`${API}/admin/assistant-orders-metrics`),
      ]);
      setOrders(list.data.orders);
      setCounts(list.data.counts);
      setMetrics(m.data);
    } catch { toast.error("Failed to load orders"); }
    setLoading(false);
  }, [tab, journey, failure, q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  return (
    <div data-testid="admin-orders-board">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h2 className="font-semibold text-gray-900 text-lg">Orders</h2>
        <a href={`${API}/admin/assistant-orders-export?tab=${tab}`}
          className="flex items-center gap-1.5 text-sm text-[#2D2766] border border-[#2D2766] rounded-lg px-3 py-1.5" data-testid="orders-export-csv">
          <Download className="w-4 h-4" /> Export CSV
        </a>
      </div>

      <MetricsStrip m={metrics} />

      {/* Sub-tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 transition-colors ${
              tab === t.id ? "bg-[#2D2766] text-white" : "bg-white border text-gray-600 hover:bg-gray-50"}`}
            data-testid={`orders-subtab-${t.id}`}>
            {t.label}
            {counts[t.id] > 0 && (
              <span className={`text-xs px-1.5 rounded-full ${t.id === "needs_attention" ? "bg-red-500 text-white" : tab === t.id ? "bg-white/25" : "bg-gray-200 text-gray-700"}`}>{counts[t.id]}</span>
            )}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <select value={journey} onChange={(e) => setJourney(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm bg-white" data-testid="orders-filter-journey">
          <option value="">All journeys</option>
          {JOURNEYS.map((j) => <option key={j} value={j}>{j.replace("_", " ")}</option>)}
        </select>
        <select value={failure} onChange={(e) => setFailure(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm bg-white" data-testid="orders-filter-failure">
          <option value="">All issues</option>
          {FAILURES.map((f) => <option key={f} value={f}>{f.replace(/_/g, " ")}</option>)}
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / phone / item"
          className="border rounded-lg px-3 py-1.5 text-sm w-52" data-testid="orders-filter-search" />
      </div>

      {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto mt-8 text-gray-400" /> :
        orders.length === 0 ? <p className="text-gray-400 text-center py-10">No orders here</p> :
        <div className="space-y-3">
          {orders.map((o) => <OrderCard key={o.order_id} o={o} onOpen={(ord) => setOpenId(ord.order_id)} />)}
        </div>}

      {openId && <OrderDetail orderId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}
